/**
 * Equipment Types — Single Source of Truth
 *
 * Trailer types and vehicle (power unit) categories shared across
 * backend-tms and web-tms. All equipment classification lives here.
 *
 * Rule 15: No duplication — import from @axon/common everywhere.
 */

// ==================== Equipment Category ====================

/**
 * Top-level classification: trailer (requires tractor) vs vehicle (self-contained cargo)
 */
export enum EquipmentCategory {
  TRAILER = 'TRAILER',
  VEHICLE = 'VEHICLE',
}

export const EQUIPMENT_CATEGORY_LABELS: Record<EquipmentCategory, string> = {
  [EquipmentCategory.TRAILER]: 'Trailer',
  [EquipmentCategory.VEHICLE]: 'Vehicle',
};

// ==================== Trailer Type ====================

/**
 * Trailer type classification (matches equipment module enum values)
 */
export enum TrailerType {
  DRY_VAN = 'dry_van',
  REEFER = 'reefer',
  FLATBED = 'flatbed',
  STEP_DECK = 'step_deck',
  LOWBOY = 'lowboy',
  TANKER = 'tanker',
  CONESTOGA = 'conestoga',
  LIVESTOCK = 'livestock',
  CAR_CARRIER = 'car_carrier',
  DUMP = 'dump',
  CONTAINER_CHASSIS = 'container_chassis',
  SPECIALIZED = 'specialized',
}

export const TRAILER_TYPE_LABELS: Record<TrailerType, string> = {
  [TrailerType.DRY_VAN]: 'Dry Van',
  [TrailerType.REEFER]: 'Refrigerated (Reefer)',
  [TrailerType.FLATBED]: 'Flatbed',
  [TrailerType.STEP_DECK]: 'Step Deck',
  [TrailerType.LOWBOY]: 'Lowboy / Double Drop',
  [TrailerType.TANKER]: 'Tanker',
  [TrailerType.CONESTOGA]: 'Conestoga',
  [TrailerType.LIVESTOCK]: 'Livestock',
  [TrailerType.CAR_CARRIER]: 'Car Carrier',
  [TrailerType.DUMP]: 'Dump Trailer',
  [TrailerType.CONTAINER_CHASSIS]: 'Container Chassis',
  [TrailerType.SPECIALIZED]: 'Specialized',
};

// ==================== Power Unit (Vehicle) Category ====================

/**
 * Power unit / vehicle category (matches equipment module enum values)
 */
export enum PowerUnitCategory {
  LIGHT_TRUCK = 'light_truck',
  MEDIUM_TRUCK = 'medium_truck',
  HEAVY_TRUCK = 'heavy_truck',
  TRACTOR = 'tractor',
}

export const POWER_UNIT_CATEGORY_LABELS: Record<PowerUnitCategory, string> = {
  [PowerUnitCategory.LIGHT_TRUCK]: 'Light Truck / Sprinter Van (Class 1-2)',
  [PowerUnitCategory.MEDIUM_TRUCK]: 'Medium Truck / Straight Truck (Class 5-6)',
  [PowerUnitCategory.HEAVY_TRUCK]: 'Heavy Truck (Class 7-8)',
  [PowerUnitCategory.TRACTOR]: 'Tractor (Class 7-8)',
};

/**
 * Vehicle types that carry cargo (excludes TRACTOR which needs a trailer)
 */
export const CARGO_VEHICLE_CATEGORIES: PowerUnitCategory[] = [
  PowerUnitCategory.LIGHT_TRUCK,
  PowerUnitCategory.MEDIUM_TRUCK,
  PowerUnitCategory.HEAVY_TRUCK,
];

/**
 * Standard trailer lengths in canonical metric units. UI forms offer these as
 * preset buttons but the DB stores the metric value on `trailers.length`.
 * Backend legality checks (see `compatibility-checker.ts`) use the same 53'
 * maximum that `SIZE_53_FT` encodes — if this const changes, those values
 * need to update together.
 */
export const TRAILER_LENGTH_PRESETS_METERS = {
  SIZE_53_FT: 16.15,
  SIZE_48_FT: 14.63,
  SIZE_45_FT: 13.72,
  SIZE_40_FT: 12.19,
  SIZE_28_FT: 8.53,
  SIZE_20_FT: 6.1,
} as const;

// ==================== Trailer Status ====================

/**
 * Operational status of a trailer. Values match the `trailer_status_enum`
 * column in Postgres — updating a member requires a migration. Loadedness
 * is expressed as its own status value (`loaded`, `loading`, `unloading`),
 * not as a modifier on `available`.
 */
export enum TrailerStatus {
  AVAILABLE = 'available',
  ASSIGNED = 'assigned',
  LOADING = 'loading',
  LOADED = 'loaded',
  IN_TRANSIT = 'in_transit',
  UNLOADING = 'unloading',
  MAINTENANCE = 'maintenance',
  INSPECTION_PENDING = 'inspection_pending',
  OUT_OF_SERVICE = 'out_of_service',
  RETIRED = 'retired',
}

// ==================== Power Unit Status ====================

/**
 * Operational status of a power unit. Values match the
 * `power_unit_status_enum` column in Postgres — updating a member requires
 * a migration.
 */
export enum PowerUnitStatus {
  AVAILABLE = 'available',
  ASSIGNED = 'assigned',
  MAINTENANCE = 'maintenance',
  OUT_OF_SERVICE = 'out_of_service',
  RETIRED = 'retired',
}

// ==================== Coupling Type ====================

/**
 * Trailer coupling mechanism. Matches the `power_units_coupling_type_enum`
 * Postgres type.
 */
export enum CouplingType {
  FIFTH_WHEEL = 'fifth_wheel',
  PINTLE_HOOK = 'pintle_hook',
  BALL_HITCH = 'ball_hitch',
  GOOSENECK = 'gooseneck',
  NO_COUPLING = 'no_coupling',
}

// ==================== Ownership Type ====================

/**
 * Equipment ownership classification for financial and operational tracking.
 */
export enum OwnershipType {
  OWNED = 'owned',
  LEASED = 'leased',
  RENTED = 'rented',
  SUBCONTRACTED = 'subcontracted',
}

export const OWNERSHIP_TYPE_LABELS: Record<OwnershipType, string> = {
  [OwnershipType.OWNED]: 'Owned',
  [OwnershipType.LEASED]: 'Leased',
  [OwnershipType.RENTED]: 'Rented',
  [OwnershipType.SUBCONTRACTED]: 'Subcontracted',
};

// ==================== Fuel Type ====================

/**
 * Power unit fuel types. Stored as varchar on the entity (not a DB enum) so
 * new values can be added without a migration.
 */
export enum FuelType {
  DIESEL = 'diesel',
  GASOLINE = 'gasoline',
  CNG = 'cng',
  LNG = 'lng',
  ELECTRIC = 'electric',
  HYBRID = 'hybrid',
}

// ==================== Odometer Unit ====================

/**
 * Unit of measure for odometer readings. USA-first default is MILES; TR
 * tenants use KILOMETERS.
 */
export enum OdometerUnit {
  MILES = 'miles',
  KILOMETERS = 'kilometers',
}
